import axios, { AxiosInstance } from 'axios';
import Auth from '@aws-amplify/auth';

import { serializeFilter } from '@/utils/filter-util';

class ApiClient {
  request: AxiosInstance;

  static instance: ApiClient;

  static axios: AxiosInstance;

  // Request interceptor for API calls
  constructor(axiosInstance: AxiosInstance) {
    this.request = axiosInstance;
  }

  get({ path, params, ...opts }: { path: string; params?: any; paramsSerializer?: any }) {
    const promise = new Promise<any>((resolve, reject) => {
      this.request.get(path, { params, ...opts }).then((response) => {
        resolve(response.data);
      }, (err) => {
        reject(err);
        return {};
      });
    });
    return promise;
  }

  post({ path, body, params }: { path: string; body?: any; params?: any; }) {
    const promise = new Promise<any>((resolve, reject) => {
      this.request.post(path, body, { params }).then((response) => {
        resolve(response.data);
      }, (err) => {
        reject(err.response ? err.response.data : err);
      });
    });
    return promise;
  }

  del({ path }: { path: string; }) {
    return this.request.delete(path) as Promise<any>;
  }

  patch({ path }: { path: string; }) {
    return this.request.patch(path) as Promise<any>;
  }

  put({ path, body }: { path: string; body: any; }) {
    const promise = new Promise<any>((resolve, reject) => {
      this.request.put(path, body).then((response) => {
        resolve(response.data);
      }, (err) => {
        reject(err.response.data);
      });
    });
    return promise;
  }

  search({ path, filter }) {
    const promise = new Promise((resolve, reject) => {
      this.request.get(filter ? path + serializeFilter(filter) : path).then((response) => {
        resolve(response.data);
      }, (err) => reject(err.response.data));
    });
    return promise;
  }

  /**
   * @param {{ path: string, filter?: IFilter, params: any }}
   * @returns
   */
  searchWithParams({ path, filter, params }) {
    const promise = new Promise((resolve, reject) => {
      this.request.get(filter ? path + serializeFilter(filter) : path, { params }).then((response) => {
        resolve(response.data);
      }, (err) => reject(err.response.data));
    });
    return promise;
  }

  searchWithParamsFilters({ path, filter, params }) {
    const promise = new Promise((resolve, reject) => {
      this.request.get(filter ? `${path}?${filter}` : path, { params }).then((response) => {
        resolve(response.data);
      }, (err) => reject(err.response.data));
    });
    return promise;
  }

  getBlob({ path, params }) {
    const promise = new Promise((resolve, reject) => {
      this.request.get(path, { params, responseType: 'blob' }).then((response) => {
        resolve(response.data);
      }, (err) => {
        reject(err.response ? err.response.data : { status: 500, message: 'Internal Server Error' });
        return {};
      });
    });
    return promise;
  }

  postBlob({ path, body }) {
    const promise = new Promise((resolve, reject) => {
      this.request.post(path, body, { responseType: 'blob' }).then((response) => {
        resolve(response.data);
      }, (err) => {
        reject(err.response.data);
      });
    });
    return promise;
  }
}

const request = axios.create({ baseURL: process.env.VUE_APP_BASE_API_URL });

request.interceptors.request.use(
  async (config) => {
    const ret = { ...config };
    // try {
    const session = await Auth.currentSession();
    const accessToken = session.getIdToken().getJwtToken();
    ret.headers = {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json, application/octet-stream',
    };
    return ret;
  },
  (error) => {
    Promise.reject(error);
  },
);

ApiClient.instance = new ApiClient(request);
ApiClient.axios = request;

export default ApiClient;
